import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useQuery } from '@tanstack/react-query';
import { fetchCommittedActivities } from '@apis/activities';
import { activityMentionRefs } from '@d-orca/shared/client/comms';
import { committedActivitiesKey } from '@constants/queryKeys';
import { retryUnlessProviderError } from '@utils/queryRetry';
import { selectSiteId } from '@store/selectors/site.selectors';
import type { CommsMember } from '@apis/comms';
import type { EntityRef, MentionKind } from '@interfaces/ICommsApi';
import { ScrollArea } from '@components/ui/ScrollArea/ScrollArea';
import { memberName } from '../../../utils/memberName';
import styles from './MentionPicker.module.css';
import { isImeComposing } from '@utils/ime';

interface IMentionPickerProps {
  members: CommsMember[];
  onPick: (ref: EntityRef) => void;
  onClose: () => void;
}

interface ICategoryItem {
  kind: MentionKind;
  labelKey: string;
}

const CATEGORIES: ICategoryItem[] = [
  { kind: 'member',   labelKey: 'comms.mention.member' },
  { kind: 'activity', labelKey: 'comms.mention.activity' },
];

const SKELETON_WIDTHS = ['68%', '52%', '74%', '46%', '60%'];

type Stage = { kind: 'category' } | { kind: 'items'; mention: MentionKind };

export function MentionPicker({ members, onPick, onClose }: IMentionPickerProps) {
  const { t } = useTranslation();
  const siteId = useSelector(selectSiteId);
  const [stage, setStage] = useState<Stage>({ kind: 'category' });
  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(0);

  const popupRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Array<HTMLLIElement | null>>([]);

  // Close on outside click. Uses mousedown so it fires before any focus
  // change and beats the item-row onMouseDown handlers when relevant.
  useEffect(() => {
    function onPointer(e: MouseEvent) {
      const popup = popupRef.current;
      if (!popup) return;
      if (e.target instanceof Node && popup.contains(e.target)) return;
      onClose();
    }
    document.addEventListener('mousedown', onPointer, true);
    return () => document.removeEventListener('mousedown', onPointer, true);
  }, [onClose]);

  // The site's committed activities — the Activities page's population, under
  // its query key so the two share a cache. Fetched only on the activity stage.
  const activitiesQuery = useQuery({
    queryKey: committedActivitiesKey(siteId ?? ''),
    queryFn: ({ signal }) => fetchCommittedActivities(siteId!, signal),
    enabled: !!siteId && stage.kind === 'items' && stage.mention === 'activity',
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    retry: retryUnlessProviderError,
  });

  const visibleCategories = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    return CATEGORIES
      .map((categoryItem) => ({ category: categoryItem, label: t(categoryItem.labelKey) }))
      .filter((entry) => !normalizedQuery || entry.label.toLowerCase().includes(normalizedQuery));
  }, [query, t]);

  const visibleItems = useMemo<EntityRef[]>(() => {
    if (stage.kind === 'category') return [];
    const normalizedQuery = query.trim().toLowerCase();
    let items: EntityRef[];
    if (stage.mention === 'member') {
      items = members.map((m) => ({ kind: 'member' as const, id: m.id, label: memberName(m) }));
    } else {
      items = activityMentionRefs(activitiesQuery.data ?? []);
    }
    return (normalizedQuery ? items.filter((item) => item.label.toLowerCase().includes(normalizedQuery)) : items).slice(0, 50);
  }, [stage, query, activitiesQuery.data, members]);

  const visibleLength = stage.kind === 'category' ? visibleCategories.length : visibleItems.length;

  // Scroll the highlighted item into view when keyboard nav moves out of frame.
  useEffect(() => {
    itemRefs.current[highlight]?.scrollIntoView({ block: 'nearest' });
  }, [highlight, stage]);

  const commitHighlight = useCallback(() => {
    if (stage.kind === 'category') {
      const entry = visibleCategories[highlight];
      if (!entry) return;
      setStage({ kind: 'items', mention: entry.category.kind });
      setQuery('');
      setHighlight(0);
    } else {
      const entry = visibleItems[highlight];
      if (!entry) return;
      onPick(entry);
    }
  }, [stage, highlight, visibleCategories, visibleItems, onPick]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        if (stage.kind === 'items') {
          setStage({ kind: 'category' });
          setQuery('');
          setHighlight(0);
        } else {
          onClose();
        }
        return;
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setHighlight((prevHighlight) => Math.min(prevHighlight + 1, Math.max(visibleLength - 1, 0)));
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHighlight((prevHighlight) => Math.max(prevHighlight - 1, 0));
        return;
      }
      if ((e.key === 'Enter' || e.key === 'Tab') && !isImeComposing(e)) {
        e.preventDefault();
        commitHighlight();
      }
    }
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [stage, visibleLength, onClose, commitHighlight]);

  function goBack() {
    setStage({ kind: 'category' });
    setQuery('');
    setHighlight(0);
  }

  function onQueryChange(value: string) {
    setQuery(value);
    setHighlight(0);
  }

  const headerLabel =
    stage.kind === 'category'
      ? t('comms.mention.title')
      : stage.mention === 'member'
        ? t('comms.mention.pickMember')
        : t('comms.mention.pickActivity');

  const isLoadingItems = stage.kind === 'items' && stage.mention === 'activity' && activitiesQuery.isPending;

  return (
    <div ref={popupRef} className={styles.popup} role="dialog" aria-label={t('comms.mention.title')}>
      <header className={styles.header}>
        <span className={styles.title}>{headerLabel}</span>
        {stage.kind === 'items' && (
          <button type="button" className={styles.backBtn} onClick={goBack}>
            {t('comms.mention.back')}
          </button>
        )}
      </header>

      <input
        autoFocus
        className={styles.search}
        value={query}
        onChange={(e) => onQueryChange(e.target.value)}
        placeholder={t('comms.mention.searchPlaceholder')}
        aria-label={t('comms.mention.searchPlaceholder')}
      />

      <ScrollArea className={styles.listScroll}>
        <ul className={styles.list} role="listbox">
          {isLoadingItems && SKELETON_WIDTHS.map((width, i) => (
            <li key={i} className={styles.skeletonItem} aria-hidden="true">
              <span className={styles.skeletonDot} />
              <span className={styles.skeletonLabel} style={{ width }} />
            </li>
          ))}
          {!isLoadingItems && visibleLength === 0 && (
            <li className={styles.empty}>{t('comms.mention.empty')}</li>
          )}
          {!isLoadingItems && stage.kind === 'category' &&
            visibleCategories.map((entry, index) => (
              <li
                key={entry.category.kind}
                ref={(el) => { itemRefs.current[index] = el; }}
                role="option"
                aria-selected={highlight === index}
                className={`${styles.item} ${highlight === index ? styles.itemActive : ''}`}
                onMouseEnter={() => setHighlight(index)}
                onMouseDown={(e) => { e.preventDefault(); setHighlight(index); commitHighlight(); }}
              >
                <span className={`${styles.kindDot} ${styles[`kind_${entry.category.kind}`]}`} />
                <span className={styles.itemLabel}>{entry.label}</span>
              </li>
            ))}
          {!isLoadingItems && stage.kind === 'items' &&
            visibleItems.map((item, index) => (
              <li
                key={`${item.kind}-${item.id}`}
                ref={(el) => { itemRefs.current[index] = el; }}
                role="option"
                aria-selected={highlight === index}
                className={`${styles.item} ${highlight === index ? styles.itemActive : ''}`}
                onMouseEnter={() => setHighlight(index)}
                onMouseDown={(e) => { e.preventDefault(); setHighlight(index); commitHighlight(); }}
              >
                <span className={`${styles.kindDot} ${styles[`kind_${item.kind}`]}`} />
                <span className={styles.itemLabel}>{item.label}</span>
              </li>
            ))}
        </ul>
      </ScrollArea>
    </div>
  );
}
